import { FeedbackModal, showToast } from "@/components/feedback"
import { NoImage } from "@/components/no-image/NoImage"
import { RailScroller } from "@/components/home/RailScroller"
import { RAIL_COLUMN, SectionRail } from "@/components/home/SectionRail"
import {
  RAIL_CTA_REM,
  RAIL_MICRO_REM,
  RAIL_NAME_REM,
  RAIL_PAD_REM,
  RAIL_PRICE_REM,
  RAIL_ROW_GAP_REM,
  STOREFRONT_ROUNDED_PX,
  STOREFRONT_SECTION_LABELS,
  railCardChrome,
  railCardShrinkRem,
  type StorefrontSection,
} from "@/domain/storefront-appearance"
import { useOptimisticCart } from "@/hooks/use-optimistic-cart"
import { useRailCardWidth } from "@/hooks/use-rail-metrics"
import { useStoreClosedGuard } from "@/hooks/use-store-closed-guard"
import { ReorderableOrder, useRecentReorderableOrders } from "@/hooks/use-recent-orders"
import { CartItem } from "@/types"
import { cn } from "@/lib/cn"
import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { Button, Card, Price, Text } from "@/components/ui"

/**
 * Horizontal reorder rail showing the last few terminal orders.
 *
 * Each card mirrors iFood's pattern:
 * - A 1/2/3/4-tile photo collage built from the items' catalog images.
 * - A short list of items prefixed with quantity.
 * - The recomputed total (live catalog prices, not historical).
 * - A primary add-to-bag CTA that triggers the reorder.
 *
 * `section` carries this rail's size (card width) and whether the collage shows.
 */
export default function ReorderLastOrderCard({ section }: { section: StorefrontSection }) {
  const orders = useRecentReorderableOrders()
  const { railRef, cardWidth } = useRailCardWidth({
    size: section.size,
    shrinkRem: railCardShrinkRem(section.size, section.imageVisible),
  })

  if (orders.length === 0) return null

  return (
    <SectionRail
      label={STOREFRONT_SECTION_LABELS.reorder.label}
      titleCard={section.titleCard}
    >
      <RailScroller
        className={RAIL_COLUMN}
        rootRef={railRef}
        scrollerClassName="snap-x snap-mandatory"
        watch={orders.length}
      >
        <ul className="flex gap-3 pb-1">
          {orders.map((entry) => (
            <li key={entry.order.id} className="snap-start shrink-0">
              {cardWidth !== null && (
                <ReorderCard
                  entry={entry}
                  section={section}
                  width={cardWidth}
                />
              )}
            </li>
          ))}
        </ul>
      </RailScroller>
    </SectionRail>
  )
}

/**
 * Names the items reorder refused to put in the cart.
 *
 * A bare count ("2 itens foram ignorados") leaves the customer hunting through
 * the menu for what is missing, so the message spells the items out and tells
 * them the only way back: add them again by hand. Long orders cap the list so
 * the toast stays readable.
 */
function buildDroppedItemsMessage(names: string[]): string {
  const MAX_NAMES = 3
  const shown = names.slice(0, MAX_NAMES).map((name) => `“${name}”`)
  const hiddenCount = names.length - shown.length
  if (hiddenCount > 0) {
    shown.push(`mais ${hiddenCount} ${hiddenCount === 1 ? "item" : "itens"}`)
  }

  const list =
    shown.length === 1
      ? shown[0]
      : `${shown.slice(0, -1).join(", ")} e ${shown[shown.length - 1]}`

  return names.length === 1
    ? `Não foi possível adicionar ${list} à sacola porque o cardápio mudou. Se quiser, adicione o item novamente pelo menu.`
    : `Não foi possível adicionar ${list} à sacola porque o cardápio mudou. Se quiser, adicione os itens novamente pelo menu.`
}
interface ReorderCardProps {
  entry: ReorderableOrder
  section: StorefrontSection
  width: number
}

function ReorderCard({
  entry,
  section,
  width,
}: ReorderCardProps) {
  const { cart, addItem, clearCart } = useOptimisticCart()
  const { ensureStoreOpen } = useStoreClosedGuard()
  const navigate = useNavigate()
  const [showReplaceConfirm, setShowReplaceConfirm] = useState(false)
  const [isWorking, setIsWorking] = useState(false)

  const { order, cartItems, droppedItemNames } = entry
  const estimatedTotal = cartItems.reduce((sum, ci) => sum + ci.itemTotalPrice, 0)
  const collageImages = cartItems
    .slice(0, 4)
    .map((ci) => ci.item.image?.URL ?? "")
    .filter(Boolean)
  const itemsToList = cartItems.slice(0, 1)
  const remainingCount = Math.max(0, cartItems.length - itemsToList.length)
  const chrome = railCardChrome(width, section.size)

  async function applyReorder() {
    if (isWorking) return
    setIsWorking(true)
    console.info("[telemetry] reorder_started", {
      orderId: order.id,
      itemCount: cartItems.length,
      droppedItemsCount: droppedItemNames.length,
    })
    try {
      if (cart.items.length > 0) await clearCart()
      for (const cartItem of cartItems) addItem(cartItem as CartItem)
      if (droppedItemNames.length > 0) {
        showToast.warning(buildDroppedItemsMessage(droppedItemNames), {
          title: droppedItemNames.length === 1 ? "Item não adicionado" : "Itens não adicionados",
          autoClose: 8000,
        })
      }
      console.info("[telemetry] reorder_completed", {
        orderId: order.id,
        itemCount: cartItems.length,
      })
      navigate("/cart")
    } catch (error) {
      console.error("[ReorderLastOrderCard] Failed to reorder:", error)
      showToast.error("Não deu pra repetir o pedido. Tente de novo.")
    } finally {
      setIsWorking(false)
      setShowReplaceConfirm(false)
    }
  }

  function handleClick() {
    console.info("[telemetry] reorder_clicked", { orderId: order.id })
    if (!ensureStoreOpen()) return
    if (cart.items.length > 0) {
      setShowReplaceConfirm(true)
      return
    }
    void applyReorder()
  }

  return (
    <>
      
      <Card
        as="article"
        padding="none"
        style={{
          width,
          height: chrome.heightPx,
          padding: `${RAIL_PAD_REM[section.size]}rem`,
          borderRadius: `${STOREFRONT_ROUNDED_PX[section.rounded]}px`,
        }}
        className="box-border flex shrink-0 gap-3 overflow-hidden"
      >
        {section.imageVisible && (
          <ImageCollage
            urls={collageImages}
            alt={`Itens do pedido ${order.id}`}
            side={`${chrome.photoPx}px`}
          />
        )}
        <div className="flex-1 min-w-0 flex flex-col">
          <ul className="flex-1 min-w-0 space-y-0.5 overflow-hidden">
            {itemsToList.map((ci) => (
              <Text
                as="li"
                key={ci.id}
                tone="muted"
                className="flex gap-2"
                style={{ fontSize: `${RAIL_NAME_REM[section.size]}rem` }}
              >
                <Text as="span" weight="semibold" className="shrink-0">
                  {ci.quantity}
                </Text>
                <Text as="span" truncate>
                  {ci.item.name}
                </Text>
              </Text>
            ))}
            <Text
              as="li"
              tone="muted"
              className={cn(remainingCount === 0 && "invisible")}
              style={{ fontSize: `${RAIL_MICRO_REM[section.size]}rem` }}
              aria-hidden={remainingCount === 0}
            >
              {remainingCount > 0
                ? `+${remainingCount} ${remainingCount === 1 ? "item" : "itens"}`
                : "\u00A0"}
            </Text>
          </ul>
          <Price
            as="p"
            value={estimatedTotal}
            weight="bold"
            tone="secondary"
            className={cn(estimatedTotal <= 0 && "invisible")}
            style={{
              fontSize: `${RAIL_PRICE_REM[section.size]}rem`,
              marginTop: `${RAIL_ROW_GAP_REM[section.size]}rem`,
            }}
            aria-hidden={estimatedTotal <= 0}
          />
          <Button
            variant="plain"
            size="auto"
            align="start"
            onClick={handleClick}
            disabled={isWorking}
            style={{
              fontSize: `${RAIL_CTA_REM[section.size]}rem`,
              marginTop: `${RAIL_ROW_GAP_REM[section.size]}rem`,
            }}
            aria-label={`Adicionar à sacola os itens do pedido de ${order.id.slice(0, 8)}`}
          >
            Adicionar à sacola
          </Button>
        </div>
      </Card>

      <FeedbackModal
        open={showReplaceConfirm}
        onOpenChange={setShowReplaceConfirm}
        variant="warning"
        title="Substituir a sacola?"
        description="Sua sacola já tem itens. Ao repetir esse pedido, eles dão lugar aos novos."
        confirmLabel="Sim, substituir"
        cancelLabel="Voltar"
        loading={isWorking}
        onConfirm={() => void applyReorder()}
      />
    </>
  )
}

/**
 * Builds a 1/2/3/4-tile photo collage of the order's items.
 *
 * Layouts (CSS grid):
 * - 1 photo  → single full tile
 * - 2 photos → two vertical halves
 * - 3 photos → one tall left half + two stacked right quarters
 * - 4 photos → 2x2 grid
 *
 * Missing images render as a neutral gray tile so the collage stays
 * geometrically consistent.
 */

interface ImageCollageProps{
  urls: string[]
  alt: string
  side: string
}
function ImageCollage({
  urls,
  alt,
  side,
}: ImageCollageProps) {
  const tiles = urls.slice(0, 4)
  const count = Math.max(1, tiles.length)

  let gridClass = "grid-cols-1 grid-rows-1"
  if (count === 2) gridClass = "grid-cols-2 grid-rows-1"
  if (count === 3) gridClass = "grid-cols-2 grid-rows-2"
  if (count === 4) gridClass = "grid-cols-2 grid-rows-2"

  return (
    <div
      style={{ width: side, height: side }}
      className={cn(
        "shrink-0 rounded-card overflow-hidden bg-gray-scale-100 grid gap-0.5",
        gridClass,
      )}
      aria-hidden="true"
    >
      {tiles.map((url, idx) => (
        <Tile
          key={`${url}-${idx}`}
          url={url}
          alt={alt}
          spanRow={count === 3 && idx === 0}
        />
      ))}
      {tiles.length === 0 ? <NoImage className="h-full w-full" /> : null}
    </div>
  )
}

function Tile({
  url,
  alt,
  spanRow,
}: {
  url: string
  alt: string
  spanRow: boolean
}) {
  if (!url) return <NoImage className="h-full w-full" />
  return (
    <img
      src={url}
      alt={alt}
      loading="lazy"
      className={cn(
        "w-full h-full object-cover bg-gray-scale-100",
        spanRow && "row-span-2",
      )}
    />
  )
}
